import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { products, categories, siteImages } from '../data/mock';
import { ShoppingCart, Filter, Grid, List, ChevronDown, SlidersHorizontal, X, Package, Bolt } from 'lucide-react';
import { useCart } from '../context/CartContext';

// Placeholder Image Component
const PlaceholderImage = ({ className = '' }) => (
  <div className={`bg-neutral-800 flex items-center justify-center ${className}`}>
    <div className="text-center">
      <Package className="w-12 h-12 text-neutral-600 mx-auto mb-2" />
      <span className="text-neutral-500 text-xs">Product Image</span>
    </div>
  </div>
);

const ShopPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('featured');
  const [showFilters, setShowFilters] = useState(false);
  const { addToCart } = useCart();
  
  const selectedCategory = searchParams.get('category');

  useEffect(() => {
    document.title = 'Muscle Growth & Performance Supplements | Axon Muscle';
  }, []);

  const filteredProducts = useMemo(() => {
    let result = [...products];
    
    if (selectedCategory) {
      result = result.filter(p => p.categorySlug === selectedCategory);
    }
    
    switch (sortBy) {
      case 'price-low':
        result.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        result.sort((a, b) => b.price - a.price);
        break;
      case 'name':
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
      default:
        break;
    }
    
    return result;
  }, [selectedCategory, sortBy]);

  const handleCategorySelect = (categorySlug) => {
    if (categorySlug) {
      setSearchParams({ category: categorySlug });
    } else {
      setSearchParams({});
    }
    setShowFilters(false);
  };

  const handleAddToCart = (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    // Add 2 items by default
    addToCart(product);
    addToCart(product);
  };

  const currentCategory = categories.find(c => c.slug === selectedCategory);

  return (
    <div className="min-h-screen bg-neutral-950">
      <Header />
      <CartDrawer />
      
      <main className="pt-[104px]">
        {/* Banner - Dark Dramatic */}
        <section className="relative h-72 lg:h-96 overflow-hidden">
          <img 
            src={siteImages.shopBanner}
            alt="Fitness supplements"
            className="w-full h-full object-cover opacity-50"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-neutral-950 via-neutral-950/85 to-neutral-950/60" />
          <div className="absolute inset-0 bg-gradient-to-t from-neutral-950 via-transparent to-transparent" />
          
          {/* Cyan Glow */}
          <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-axon-600/15 rounded-full blur-[120px]" />
          
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <div className="max-w-2xl">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-axon-600/20 border border-axon-500/30 rounded-full text-axon-400 text-sm font-semibold mb-6">
                  <Bolt className="w-4 h-4" />
                  PREMIUM QUALITY
                </div>
                <h1 className="font-display text-4xl lg:text-5xl text-white uppercase tracking-tight mb-4">
                  {currentCategory ? currentCategory.name : 'All Supplements'}
                </h1>
                <p className="text-neutral-300 text-lg">
                  {currentCategory ? currentCategory.description : 'Browse our complete collection of premium performance supplements'}
                </p>
              </div>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar - Desktop */}
            <aside className="hidden lg:block w-64 flex-shrink-0">
              <div className="bg-neutral-900 rounded-2xl border border-neutral-800 p-6 sticky top-[130px]">
                <h3 className="font-display font-bold text-white uppercase tracking-wider mb-5 flex items-center gap-2">
                  <SlidersHorizontal className="w-4 h-4 text-axon-500" />
                  Categories
                </h3>
                <div className="space-y-1">
                  <button
                    onClick={() => handleCategorySelect(null)}
                    className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                      !selectedCategory 
                        ? 'bg-axon-600 text-white' 
                        : 'text-neutral-400 hover:bg-neutral-800 hover:text-white'
                    }`}
                  >
                    All Products
                  </button>
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => handleCategorySelect(cat.slug)}
                      className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                        selectedCategory === cat.slug 
                          ? 'bg-axon-600 text-white' 
                          : 'text-neutral-400 hover:bg-neutral-800 hover:text-white'
                      }`}
                    >
                      {cat.name}
                      <span className="ml-2 text-xs opacity-60">({cat.productCount})</span>
                    </button>
                  ))}
                </div>
              </div>
            </aside>

            {/* Main Content */}
            <div className="flex-1">
              {/* Toolbar */}
              <div className="flex flex-wrap items-center justify-between gap-4 mb-8 bg-neutral-900 rounded-2xl border border-neutral-800 p-4">
                <div className="flex items-center gap-3">
                  {/* Mobile Filter Toggle */}
                  <button
                    onClick={() => setShowFilters(true)}
                    className="lg:hidden flex items-center gap-2 px-4 py-2.5 bg-neutral-800 rounded-xl text-white font-medium text-sm"
                  >
                    <Filter className="w-4 h-4" />
                    Filters
                  </button>
                  
                  <span className="text-neutral-400 text-sm">
                    <span className="font-semibold text-white">{filteredProducts.length}</span> products
                  </span>
                </div>
                
                <div className="flex items-center gap-3">
                  {/* Sort */}
                  <div className="relative">
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="appearance-none pl-4 pr-10 py-2.5 bg-neutral-800 border border-neutral-700 rounded-xl text-white text-sm font-medium focus:ring-2 focus:ring-axon-500 focus:border-axon-500 cursor-pointer"
                    >
                      <option value="featured">Featured</option>
                      <option value="price-low">Price: Low to High</option>
                      <option value="price-high">Price: High to Low</option>
                      <option value="name">Name A-Z</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 pointer-events-none" />
                  </div>
                  
                  {/* View Toggle */}
                  <div className="hidden sm:flex items-center bg-neutral-800 rounded-xl p-1">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-2 rounded-lg transition-colors ${
                        viewMode === 'grid' ? 'bg-axon-600 text-white' : 'text-neutral-400 hover:text-white'
                      }`}
                    >
                      <Grid className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`p-2 rounded-lg transition-colors ${
                        viewMode === 'list' ? 'bg-axon-600 text-white' : 'text-neutral-400 hover:text-white'
                      }`}
                    >
                      <List className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Products Grid */}
              <div className={`grid gap-6 ${
                viewMode === 'grid' 
                  ? 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-3' 
                  : 'grid-cols-1'
              }`}>
                {filteredProducts.map((product) => (
                  <Link
                    key={product.id}
                    to={`/product/${product.slug}`}
                    className={`group bg-neutral-900 rounded-2xl border border-neutral-800 hover:border-axon-500/50 transition-all duration-300 overflow-hidden hover:shadow-card-hover ${
                      viewMode === 'list' ? 'flex' : ''
                    }`}
                  >
                    <div className={`relative overflow-hidden bg-neutral-800 flex items-center justify-center p-4 ${
                      viewMode === 'list' ? 'w-48 flex-shrink-0' : 'aspect-square'
                    }`}>
                      {product.image ? (
                        <img 
                          src={product.image}
                          alt={product.name}
                          className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <PlaceholderImage className="w-full h-full" />
                      )}
                      {product.badge && (
                        <span className="absolute top-3 left-3 px-3 py-1 bg-axon-600 text-white text-xs font-bold uppercase tracking-wider rounded-lg">
                          {product.badge}
                        </span>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-neutral-900 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <div className={`p-5 ${viewMode === 'list' ? 'flex-1 flex flex-col justify-center' : ''}`}>
                      <p className="text-xs text-axon-500 uppercase tracking-wider font-semibold mb-2">
                        {categories.find(c => c.id === product.categoryId)?.name}
                      </p>
                      <h3 className="font-semibold text-white mb-2 group-hover:text-axon-400 transition-colors line-clamp-2 min-h-[48px]">
                        {product.name}
                      </h3>
                      {/* Specs Preview */}
                      <p className="text-xs text-neutral-500 mb-3">
                        {product.specs?.netWeight || product.specs?.quantity || product.specs?.includes}
                      </p>
                      {viewMode === 'list' && (
                        <p className="text-neutral-400 text-sm mb-4 line-clamp-2">{product.description}</p>
                      )}
                      <div className="flex items-center justify-between pt-4 border-t border-neutral-800">
                        <div>
                          <span className="text-xl font-bold text-white">
                            ${product.price.toFixed(2)}
                          </span>
                          <span className="block text-xs text-green-400 mt-1">In Stock</span>
                        </div>
                        <button
                          onClick={(e) => handleAddToCart(e, product)}
                          className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-axon-600 to-axon-500 hover:from-axon-500 hover:to-axon-400 text-white text-sm font-semibold rounded-xl transition-all shadow-button hover:shadow-glow-cyan"
                        >
                          <ShoppingCart className="w-4 h-4" />
                          Add
                        </button>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {filteredProducts.length === 0 && (
                <div className="text-center py-20 bg-neutral-900 rounded-2xl border border-neutral-800">
                  <div className="w-20 h-20 bg-neutral-800 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Package className="w-10 h-10 text-neutral-600" />
                  </div>
                  <h3 className="text-white font-display font-bold text-xl uppercase tracking-wide mb-2">No products found</h3>
                  <p className="text-neutral-400 mb-8">Try selecting a different category</p>
                  <button
                    onClick={() => handleCategorySelect(null)}
                    className="px-8 py-3 bg-axon-600 text-white font-bold uppercase tracking-wider rounded-xl hover:bg-axon-500 transition-colors"
                  >
                    View All Products
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Filter Drawer */}
        {showFilters && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowFilters(false)} />
            <div className="absolute right-0 top-0 bottom-0 w-80 bg-neutral-900 p-6 overflow-y-auto border-l border-neutral-800">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-display font-bold text-white uppercase tracking-wider">Filters</h3>
                <button onClick={() => setShowFilters(false)} className="p-2 hover:bg-neutral-800 rounded-lg text-neutral-400">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-1">
                <button
                  onClick={() => handleCategorySelect(null)}
                  className={`w-full text-left px-4 py-3 rounded-xl font-medium transition-colors ${
                    !selectedCategory ? 'bg-axon-600 text-white' : 'text-neutral-400 hover:bg-neutral-800'
                  }`}
                >
                  All Products
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleCategorySelect(cat.slug)}
                    className={`w-full text-left px-4 py-3 rounded-xl font-medium transition-colors ${
                      selectedCategory === cat.slug ? 'bg-axon-600 text-white' : 'text-neutral-400 hover:bg-neutral-800'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>
      
      <Footer />
    </div>
  );
};

export default ShopPage;
